import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'motion/react'

const BACKGROUND_BLUE = '16, 56, 77' // #10384d

export function AmbientBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d', { alpha: true, desynchronized: true })
    if (!canvas || !context) return

    let width = 0
    let height = 0
    let frame = 0
    let resizeFrame = 0
    let lastDraw = -Infinity
    let frameInterval = 1000 / 30
    let fieldGradient: CanvasGradient
    let lineGradient: CanvasGradient

    const resize = () => {
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const renderScale = Math.min(0.65, 960 / viewportWidth, 640 / viewportHeight)

      width = Math.max(1, Math.round(viewportWidth * renderScale))
      height = Math.max(1, Math.round(viewportHeight * renderScale))
      canvas.width = width
      canvas.height = height
      canvas.style.width = `${viewportWidth}px`
      canvas.style.height = `${viewportHeight}px`
      frameInterval = 1000 / (viewportWidth < 768 ? 24 : 30)

      fieldGradient = context.createLinearGradient(0, height * 0.26, 0, height)
      fieldGradient.addColorStop(0, `rgba(${BACKGROUND_BLUE}, 0)`)
      fieldGradient.addColorStop(0.5, `rgba(${BACKGROUND_BLUE}, 0.04)`)
      fieldGradient.addColorStop(1, `rgba(${BACKGROUND_BLUE}, 0.58)`)

      lineGradient = context.createLinearGradient(0, height * 0.25, 0, height)
      lineGradient.addColorStop(0, `rgba(${BACKGROUND_BLUE}, 0)`)
      lineGradient.addColorStop(0.56, `rgba(${BACKGROUND_BLUE}, 0.42)`)
      lineGradient.addColorStop(1, `rgba(${BACKGROUND_BLUE}, 0.92)`)
    }

    const waveHeight = (x: number, time: number) => {
      const normalizedX = x / Math.max(width, 1)
      return height * (
        0.52
        + Math.sin((normalizedX * 2.15 + time * 0.11) * Math.PI) * 0.075
        + Math.sin((normalizedX * 5.4 - time * 0.07) * Math.PI) * 0.028
      )
    }

    const draw = (timestamp: number) => {
      if (!reduceMotion && timestamp - lastDraw < frameInterval) {
        frame = window.requestAnimationFrame(draw)
        return
      }

      lastDraw = timestamp
      const time = timestamp * 0.001
      context.clearRect(0, 0, width, height)

      context.beginPath()
      context.moveTo(0, height)
      for (let x = 0; x <= width + 10; x += 10) {
        context.lineTo(x, waveHeight(x, time))
      }
      context.lineTo(width, height)
      context.closePath()
      context.fillStyle = fieldGradient
      context.globalAlpha = 0.34
      context.fill()

      const spacing = Math.max(7, width / 105)
      context.strokeStyle = lineGradient

      for (let x = -spacing; x <= width + spacing; x += spacing) {
        const pulse = (Math.sin(x * 0.043 + time * 0.8) + 1) / 2
        const crest = waveHeight(x, time) + Math.sin(x * 0.071 - time * 0.45) * 8
        context.beginPath()
        context.moveTo(x, height + 4)
        context.lineTo(x, crest)
        context.globalAlpha = 0.22 + pulse * 0.46
        context.lineWidth = 0.65 + pulse * 0.55
        context.stroke()
      }

      context.globalAlpha = 1
      if (!reduceMotion) frame = window.requestAnimationFrame(draw)
    }

    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame)
      resizeFrame = window.requestAnimationFrame(() => {
        resize()
        if (reduceMotion) draw(0)
      })
    }

    const handleVisibility = () => {
      window.cancelAnimationFrame(frame)
      if (!document.hidden && !reduceMotion) {
        lastDraw = -Infinity
        frame = window.requestAnimationFrame(draw)
      }
    }

    resize()
    draw(0)
    window.addEventListener('resize', handleResize, { passive: true })
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      window.cancelAnimationFrame(frame)
      window.cancelAnimationFrame(resizeFrame)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [reduceMotion])

  return (
    <div className="ambient-background" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  )
}
